import type { Doc } from '@blocksuite/store';
import { notify, Tabs } from '@pique/component';
import { useDocCollectionPageTitle } from '@pique/core/hooks/use-block-suite-workspace-page-title';
import { getShareUrl } from '@pique/core/modules/cloud/services/fetch';
import {
  getLocalDocShareInfo,
  ShareInfoService,
  shareLocalDoc,
  stopSharingLocalDoc,
} from '@pique/core/modules/share-doc';
import { posthog } from '@pique/core/posthog';
import { writeToClipboard } from '@pique/core/utils';
import { WorkspaceFlavour } from '@pique/env/workspace';
import { PublicPageMode } from '@pique/graphql';
import { useI18n } from '@pique/i18n';
import {
  useLiveData,
  useService,
  type WorkspaceMetadata,
  WorkspaceService,
  WorkspacesService,
} from '@toeverything/infra';
import { Check, Link, Loader2 } from 'lucide-react';
import {
  memo,
  type PropsWithChildren,
  useCallback,
  useEffect,
  useState,
} from 'react';

import * as styles from './index.css';
import { ShareExport } from './share-export';
import { SharePage } from './share-page';

export interface ShareMenuProps extends PropsWithChildren {
  workspaceMetadata: WorkspaceMetadata;
  currentPage: Doc;
  sharePageId?: string;
  compact?: boolean;
  onEnableAffineCloud: () => void;
  onOpenShareModal?: (open: boolean) => void;
}

type CopyState = 'idle' | 'loading' | 'success';

function isShareTitleReady(title: string | null | undefined): boolean {
  const trimmed = title?.trim();
  return !!trimmed && trimmed.toLowerCase() !== 'untitled';
}

export const ShareMenuContent = memo(function ShareMenuContent(
  props: ShareMenuProps
) {
  const t = useI18n();
  return (
    <div className={styles.containerStyle}>
      <Tabs.Root defaultValue="share">
        <Tabs.List>
          <Tabs.Trigger value="share">
            {t['com.pique.share-menu.shareButton']()}
          </Tabs.Trigger>
          <Tabs.Trigger value="export">{t['Export']()}</Tabs.Trigger>
        </Tabs.List>
        <Tabs.Content value="share">
          <SharePage {...props} />
        </Tabs.Content>
        <Tabs.Content value="export">
          <ShareExport />
        </Tabs.Content>
      </Tabs.Root>
    </div>
  );
});

const LocalShareButton = memo(function LocalShareButton(props: ShareMenuProps) {
  const t = useI18n();
  const sharePageId = props.sharePageId ?? props.currentPage.id;
  const [copyState, setCopyState] = useState<CopyState>('idle');
  const [isStopSharing, setIsStopSharing] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [localShareInfo, setLocalShareInfo] = useState(() =>
    getLocalDocShareInfo(props.workspaceMetadata.id, sharePageId)
  );
  const workspacesService = useService(WorkspacesService);
  const workspaceService = useService(WorkspaceService);
  const docTitle = useDocCollectionPageTitle(
    workspaceService.workspace.docCollection,
    sharePageId
  );
  const isReady = isShareTitleReady(docTitle);

  const isShared = !!localShareInfo;

  useEffect(() => {
    setLocalShareInfo(
      getLocalDocShareInfo(props.workspaceMetadata.id, sharePageId)
    );
  }, [props.workspaceMetadata.id, sharePageId]);

  const handleCopyClick = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      if (copyState === 'loading' || isStopSharing) return;

      const currentShareInfo = getLocalDocShareInfo(
        props.workspaceMetadata.id,
        sharePageId
      );
      const isNewShare = !currentShareInfo;
      posthog.track('share_copy_link_clicked', {
        workspaceType: 'local',
        isNewShare,
      });

      setCopyState('loading');
      try {
        props.onOpenShareModal?.(true);
        const result = await shareLocalDoc(
          workspacesService,
          workspaceService.workspace,
          sharePageId
        );
        const shareUrl = result.shareUrl;
        setLocalShareInfo(
          getLocalDocShareInfo(props.workspaceMetadata.id, sharePageId)
        );

        const copied = await writeToClipboard(shareUrl);
        if (copied) {
          setCopyState('success');
          notify.success({ title: t['Copied link to clipboard']() });
          posthog.track('share_copy_link_success', {
            workspaceType: 'local',
            isNewShare,
          });
          if (isNewShare) {
            posthog.track('share_link_created', {
              context: 'share_panel',
              mode: 'page',
            });
          } else {
            posthog.track('share_link_copied', {
              context: 'share_panel',
              type: 'doc',
            });
          }
        } else {
          setCopyState('idle');
          posthog.track('share_copy_link_failed', {
            workspaceType: 'local',
            reason: 'clipboard_write_failed',
          });
        }
      } catch (error) {
        setCopyState('idle');
        notify.error({
          title:
            t[
              'com.pique.share-menu.create-public-link.notification.fail.title'
            ](),
        });
        posthog.captureException(error, {
          context: 'local_share_copy_link',
          isNewShare,
        });
        return;
      }

      setTimeout(() => setCopyState('idle'), 1500);
    },
    [
      copyState,
      isStopSharing,
      workspacesService,
      workspaceService.workspace,
      props.currentPage.id,
      sharePageId,
      props.workspaceMetadata.id,
      props.onOpenShareModal,
      t,
    ]
  );

  const handleStopSharing = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      if (isStopSharing) return;

      setIsStopSharing(true);
      posthog.track('share_stopped', { workspaceType: 'local' });
      try {
        await stopSharingLocalDoc(
          workspacesService,
          props.workspaceMetadata.id,
          sharePageId
        );
        setLocalShareInfo(null);
        notify.success({
          title:
            t[
              'com.pique.share-menu.disable-publish-link.notification.success.title'
            ](),
        });
      } catch (error) {
        notify.error({
          title:
            t[
              'com.pique.share-menu.disable-publish-link.notification.fail.title'
            ](),
        });
        posthog.captureException(error, {
          context: 'local_share_stop_sharing',
        });
      } finally {
        setIsStopSharing(false);
      }
    },
    [
      isStopSharing,
      workspacesService,
      props.workspaceMetadata.id,
      props.currentPage.id,
      sharePageId,
      t,
    ]
  );

  return (
    <div
      className={`${styles.shareButtonContainer} ${isHovered && isShared ? styles.shareButtonContainerExpanded : ''}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div
        className={styles.shareIconButton}
        data-ready={isReady}
        data-shared={isShared}
        onClick={e => {
          handleCopyClick(e).catch(error => {
            posthog.captureException(error, {
              context: 'local_share_copy_click_handler',
            });
          });
        }}
      >
        {copyState === 'loading' ? (
          <Loader2
            className={styles.shareIconButtonLoading}
            style={{ width: '14px', height: '14px' }}
          />
        ) : copyState === 'success' ? (
          <Check
            className={styles.shareIconButtonSuccess}
            style={{ width: '14px', height: '14px' }}
          />
        ) : (
          <Link style={{ width: '14px', height: '14px', color: 'inherit' }} />
        )}
        {!props.compact && (
          <span className={styles.shareButtonText}>
            {t['com.pique.share-menu.shareButton']()}
          </span>
        )}
      </div>
      {isShared && !props.compact && (
        <div
          className={`${styles.stopSharingButton} ${isHovered ? styles.stopSharingButtonVisible : ''}`}
          onClick={e => {
            handleStopSharing(e).catch(error => {
              posthog.captureException(error, {
                context: 'local_share_stop_click_handler',
              });
            });
          }}
        >
          {t['com.pique.share-menu.stop-sharing']()}
        </div>
      )}
    </div>
  );
});

const CloudShareButton = memo(function CloudShareButton(props: ShareMenuProps) {
  const t = useI18n();
  const [copyState, setCopyState] = useState<CopyState>('idle');
  const [isStopSharing, setIsStopSharing] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const shareInfoService = useService(ShareInfoService);
  const workspaceService = useService(WorkspaceService);
  const sourceIsShared = useLiveData(shareInfoService.shareInfo.isShared$);
  const sharePageId = props.sharePageId ?? props.currentPage.id;
  const [targetIsShared, setTargetIsShared] = useState<boolean | null>(null);
  const docTitle = useDocCollectionPageTitle(
    workspaceService.workspace.docCollection,
    sharePageId
  );
  const isReady = isShareTitleReady(docTitle);
  const isShared =
    sharePageId === props.currentPage.id ? sourceIsShared : targetIsShared;

  useEffect(() => {
    if (sharePageId === props.currentPage.id) {
      setTargetIsShared(null);
      return undefined;
    }

    const controller = new AbortController();
    shareInfoService.shareInfo
      .getShareInfoForDoc(sharePageId, controller.signal)
      .then(info => {
        setTargetIsShared(!!info);
      })
      .catch(error => {
        if (!controller.signal.aborted) {
          posthog.captureException(error, {
            context: 'cloud_share_target_revalidation',
          });
          setTargetIsShared(null);
        }
      });

    return () => {
      controller.abort();
    };
  }, [props.currentPage.id, shareInfoService.shareInfo, sharePageId]);

  const handleCopyClick = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      if (copyState === 'loading' || isStopSharing) return;

      const isNewShare = isShared !== true;
      posthog.track('share_copy_link_clicked', {
        workspaceType: 'cloud',
        isNewShare,
      });

      setCopyState('loading');
      try {
        props.onOpenShareModal?.(true);
        await shareInfoService.shareInfo.enableShareForDoc(
          sharePageId,
          PublicPageMode.Page
        );
        if (sharePageId !== props.currentPage.id) {
          setTargetIsShared(true);
        }

        const shareUrl = getShareUrl(props.workspaceMetadata.id, sharePageId);
        const copied = await writeToClipboard(shareUrl);

        if (copied) {
          setCopyState('success');
          notify.success({ title: t['Copied link to clipboard']() });
          posthog.track('share_copy_link_success', {
            workspaceType: 'cloud',
            isNewShare,
          });
          if (isNewShare) {
            posthog.track('share_link_created', {
              context: 'share_panel',
              mode: 'page',
            });
          } else {
            posthog.track('share_link_copied', {
              context: 'share_panel',
              type: 'doc',
            });
          }
        } else {
          setCopyState('idle');
          posthog.track('share_copy_link_failed', {
            workspaceType: 'cloud',
            reason: 'clipboard_write_failed',
          });
        }
      } catch (error) {
        setCopyState('idle');
        notify.error({
          title:
            t[
              'com.pique.share-menu.create-public-link.notification.fail.title'
            ](),
        });
        posthog.captureException(error, {
          context: 'cloud_share_copy_link',
          isNewShare,
        });
        return;
      }

      setTimeout(() => setCopyState('idle'), 1500);
    },
    [
      copyState,
      isStopSharing,
      isShared,
      shareInfoService.shareInfo,
      props.workspaceMetadata.id,
      props.currentPage.id,
      props.onOpenShareModal,
      sharePageId,
      t,
    ]
  );

  const handleStopSharing = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      if (isStopSharing) return;

      setIsStopSharing(true);
      posthog.track('share_stopped', { workspaceType: 'cloud' });
      try {
        await shareInfoService.shareInfo.disableShareForDoc(sharePageId);
        if (sharePageId !== props.currentPage.id) {
          setTargetIsShared(false);
        }
        notify.success({
          title:
            t[
              'com.pique.share-menu.disable-publish-link.notification.success.title'
            ](),
        });
      } catch (error) {
        notify.error({
          title:
            t[
              'com.pique.share-menu.disable-publish-link.notification.fail.title'
            ](),
        });
        posthog.captureException(error, {
          context: 'cloud_share_stop_sharing',
        });
      } finally {
        setIsStopSharing(false);
      }
    },
    [
      isStopSharing,
      props.currentPage.id,
      sharePageId,
      shareInfoService.shareInfo,
      t,
    ]
  );

  return (
    <div
      className={`${styles.shareButtonContainer} ${isHovered && isShared ? styles.shareButtonContainerExpanded : ''}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div
        className={styles.shareIconButton}
        data-ready={isReady}
        data-shared={isShared}
        onClick={e => {
          handleCopyClick(e).catch(error => {
            posthog.captureException(error, {
              context: 'cloud_share_copy_click_handler',
            });
          });
        }}
      >
        {copyState === 'loading' ? (
          <Loader2
            className={styles.shareIconButtonLoading}
            style={{ width: '14px', height: '14px' }}
          />
        ) : copyState === 'success' ? (
          <Check
            className={styles.shareIconButtonSuccess}
            style={{ width: '14px', height: '14px' }}
          />
        ) : (
          <Link style={{ width: '14px', height: '14px', color: 'inherit' }} />
        )}
        {!props.compact && (
          <span className={styles.shareButtonText}>
            {t['com.pique.share-menu.shareButton']()}
          </span>
        )}
      </div>
      {isShared && !props.compact && (
        <div
          className={`${styles.stopSharingButton} ${isHovered ? styles.stopSharingButtonVisible : ''}`}
          onClick={e => {
            handleStopSharing(e).catch(error => {
              posthog.captureException(error, {
                context: 'cloud_share_stop_click_handler',
              });
            });
          }}
        >
          {t['com.pique.share-menu.stop-sharing']()}
        </div>
      )}
    </div>
  );
});

export const ShareMenu = memo(function ShareMenu(props: ShareMenuProps) {
  const { workspaceMetadata } = props;

  if (workspaceMetadata.flavour === WorkspaceFlavour.LOCAL) {
    return props.children || <LocalShareButton {...props} />;
  }
  return props.children || <CloudShareButton {...props} />;
});
